import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFn } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';

import {
  navigateToConfigurationSession,
  resolveNextSessionAfterLastAction,
} from 'src/features/forecastPaginated/forecastPaginatedActions/hooks/forecastPaginatedLastActionUtils';
import type { SessionSummary } from 'src/services/api/getForecastSessions';

type HandleForecastPaginatedLastActionSuccessParams = {
  closeWarningModal: () => void;
  navigate: NavigateFn;
  openRenameSession: (session: SessionSummary, initialName?: string) => void;
  previousSessions: SessionSummary[];
  queryClient: QueryClient;
  renameSessionAndNavigateToConfiguration: (
    session: SessionSummary,
    sessionName: string,
  ) => Promise<void>;
  requestedNextSessionName: string;
  setForecastSessionRedirectSuppressed: (isSuppressed: boolean) => void;
  t: TFunction<'forecast'>;
};

type ResolvedSessionParams = Pick<
  HandleForecastPaginatedLastActionSuccessParams,
  | 'closeWarningModal'
  | 'navigate'
  | 'openRenameSession'
  | 'renameSessionAndNavigateToConfiguration'
  | 'requestedNextSessionName'
  | 'setForecastSessionRedirectSuppressed'
  | 't'
> & {
  isNewSession: boolean;
  shouldNameNextSession: boolean;
  targetSession?: SessionSummary;
};

type ResolvedNextSession = Awaited<
  ReturnType<typeof resolveNextSessionAfterLastAction>
>;

function setRedirectSuppressed(
  setForecastSessionRedirectSuppressed: (isSuppressed: boolean) => void,
  isSuppressed: boolean,
) {
  setForecastSessionRedirectSuppressed(isSuppressed);
}

function showRenameError(t: TFunction<'forecast'>) {
  toast.error(t('Error'), {
    description: t('Failed to rename session'),
  });
}

function showPrepareError(t: TFunction<'forecast'>) {
  toast.error(t('Error'), {
    description: t('Failed to prepare the next session'),
  });
}

async function handleNewSession(params: ResolvedSessionParams) {
  if (!params.targetSession) return;
  if (!params.shouldNameNextSession) {
    params.openRenameSession(params.targetSession);
    return;
  }

  try {
    await params.renameSessionAndNavigateToConfiguration(
      params.targetSession,
      params.requestedNextSessionName,
    );
  } catch {
    showRenameError(params.t);
    params.openRenameSession(
      params.targetSession,
      params.requestedNextSessionName,
    );
  }
}

async function handleResolvedSession(params: ResolvedSessionParams) {
  params.closeWarningModal();
  if (!params.targetSession) {
    setRedirectSuppressed(params.setForecastSessionRedirectSuppressed, false);
    return;
  }
  if (params.isNewSession) {
    await handleNewSession(params);
    return;
  }
  setRedirectSuppressed(params.setForecastSessionRedirectSuppressed, false);
  await navigateToConfigurationSession(
    params.navigate,
    params.targetSession.id,
  );
}

function buildResolvedSessionParams(
  params: HandleForecastPaginatedLastActionSuccessParams,
  resolvedSession: ResolvedNextSession,
  shouldNameNextSession: boolean,
): ResolvedSessionParams {
  return {
    closeWarningModal: params.closeWarningModal,
    isNewSession: resolvedSession.isNewSession,
    navigate: params.navigate,
    openRenameSession: params.openRenameSession,
    renameSessionAndNavigateToConfiguration:
      params.renameSessionAndNavigateToConfiguration,
    requestedNextSessionName: params.requestedNextSessionName,
    setForecastSessionRedirectSuppressed:
      params.setForecastSessionRedirectSuppressed,
    shouldNameNextSession,
    t: params.t,
    targetSession: resolvedSession.targetSession,
  };
}

async function resolveAndHandleSession(
  params: HandleForecastPaginatedLastActionSuccessParams,
  shouldNameNextSession: boolean,
) {
  const resolvedSession = await resolveNextSessionAfterLastAction(
    params.queryClient,
    params.previousSessions,
  );
  await handleResolvedSession(
    buildResolvedSessionParams(params, resolvedSession, shouldNameNextSession),
  );
}

export async function handleForecastPaginatedLastActionSuccess(
  params: HandleForecastPaginatedLastActionSuccessParams,
) {
  const shouldNameNextSession = params.previousSessions.length === 1;
  setRedirectSuppressed(
    params.setForecastSessionRedirectSuppressed,
    shouldNameNextSession,
  );

  try {
    await resolveAndHandleSession(params, shouldNameNextSession);
  } catch {
    setRedirectSuppressed(params.setForecastSessionRedirectSuppressed, false);
    showPrepareError(params.t);
  }
}
